import _ from 'lodash';
import moment from 'moment';
import isMobilePhone from 'validator/lib/isMobilePhone';
import { getAgeRestriction } from 'shared/helpers/age-restrictions';
import { getQuantityRemaining } from 'shared/helpers/products';
import { checkAllLimits } from 'shared/helpers/purchase-limits';
import {
  isMultiSubcategoryPurchaseLimitsEnabled,
  isConfigurablePurchaseLimitsEnabled,
} from 'state/purchase-limits-flag';
import { getScheduledOrderTime } from 'shared/helpers/orders';
import { birthdayRegEx } from 'components/forms/form-utils';
import { cartItemId } from 'utils/helpers/product';

const anchoredBirthdayRegEx = new RegExp(`^${birthdayRegEx.source}$`);

/**
 * Validates inline auth form fields for signup or login.
 *
 * @param {Object} params
 * @param {string} params.inlineAuthType - 'signup' or 'login'
 * @param {Object} params.inlineAuth - form field values {firstName, lastName, phone, birthdate, password}
 * @param {string} params.dispensaryState - dispensary location state (e.g. 'OR', 'CO')
 * @param {Object} [params.dispensary] - active dispensary for location-level age overrides
 * @param {boolean} params.medicalOrder - whether this is a medical order
 * @param {boolean} params.isMobile - true when viewport < 768px
 * @param {boolean} [params.skipOnlineAgeVerification] - dispensary opt-in (super admin only) to skip
 *   the minimum age check for MEDICAL orders only; has no effect on recreational orders
 * @param {Object} [params.now] - moment instance for deterministic testing; defaults to moment()
 * @returns {{ proceed: boolean, errors?: Array<{message: string, card: string, field: string}> }}
 */
export function validateInlineAuth({
  inlineAuthType,
  inlineAuth = {},
  dispensaryState,
  dispensary,
  medicalOrder,
  isMobile,
  skipOnlineAgeVerification,
  now,
}) {
  const errors = [];

  if (inlineAuthType === 'signup') {
    if (_.isEmpty(_.get(inlineAuth, 'firstName'))) {
      errors.push({
        message: isMobile ? `First name` : `Please enter your first name`,
        card: `inlineAuth`,
        field: `firstName`,
      });
    }

    if (_.isEmpty(_.get(inlineAuth, 'lastName'))) {
      errors.push({
        message: isMobile ? `Last name` : `Please enter your last name`,
        card: `inlineAuth`,
        field: `lastName`,
      });
    }

    const phoneNumber = _.get(inlineAuth, 'phone');
    if (_.isEmpty(phoneNumber) || !isMobilePhone(phoneNumber, `en-US`)) {
      errors.push({
        message: `Please enter a valid phone number`,
        card: `inlineAuth`,
        field: `phone`,
      });
    }

    const birthdateRaw = _.get(inlineAuth, `birthdate`, ``).trim();
    const customerBirthday = moment(birthdateRaw, `MM/DD/YYYY`, true);
    const birthdateLooksValid = anchoredBirthdayRegEx.test(birthdateRaw) && customerBirthday.isValid();

    if (!birthdateLooksValid) {
      errors.push({
        message: `Please enter a valid birthday`,
        card: `inlineAuth`,
        field: `birthdate`,
      });
    } else {
      // Super-admin-gated exception: applies to medical orders only (see birthday.js on the
      // server) — there is no legal basis for a minor to purchase recreational cannabis.
      const ageRestriction = getAgeRestriction(
        { country: 'US', region: dispensaryState },
        {
          dispensary,
          configurablePurchaseLimitsFeatureEnabled: isConfigurablePurchaseLimitsEnabled(dispensary?.id),
        }
      );
      const minAge =
        skipOnlineAgeVerification && medicalOrder ? 0 : ageRestriction[medicalOrder ? 'medMinAge' : 'recMinAge'];
      const currentMoment = now || moment();
      const legalBirthday = currentMoment.clone().subtract(minAge, `years`).startOf(`day`);

      if (customerBirthday.clone().startOf(`day`).isAfter(legalBirthday)) {
        errors.push({
          message: `You must be at least ${minAge} to order.`,
          card: `inlineAuth`,
          field: `birthdate`,
        });
      }
    }
  }

  if (_.get(inlineAuth, 'password', '').length < 8) {
    errors.push({
      message: `Enter a Password (must be at least 8 characters)`,
      card: `inlineAuth`,
      field: `password`,
    });
  }

  if (errors.length > 0) {
    return {
      proceed: false,
      errors,
    };
  }

  return { proceed: true };
}

/**
 * Filters and formats available reservation time slots for checkout.
 *
 * @param {Object} params
 * @param {Object} params.reservationsAvailableByOrderType - raw reservations keyed by order type
 * @param {string} params.timezone - dispensary timezone
 * @param {Object} params.orderTypesConfigV2 - dispensary order type config
 * @param {string} params.orderType - current order type ('pickup', 'delivery', etc.)
 * @param {string|null} params.deliveryAreaId - resolved delivery area ID
 * @param {Object} [params.now] - moment instance for deterministic testing; defaults to moment()
 * @returns {Array<{dayOfWeek: string, value: number, labelPrefix: string, label: string, isDisabled: boolean, startTimeISO: string, isNextDaySlot: boolean, reservation: Object}>}
 */
export function getCheckoutReservationOptions({
  reservationsAvailableByOrderType = {},
  timezone,
  orderTypesConfigV2 = {},
  orderType,
  deliveryAreaId,
  now,
}) {
  if (!_.some(orderTypesConfigV2, ({ enableScheduledOrdering }) => enableScheduledOrdering)) {
    return [];
  }

  const normalizedOrderType = orderType === 'pickup' ? `inStorePickup` : orderType;
  const currentMoment = now || moment();
  let reservations = null;

  if (normalizedOrderType === 'delivery') {
    reservations = _.filter(
      reservationsAvailableByOrderType?.[normalizedOrderType] || [],
      ({ startTimeISO, deliveryAreaId: slotDeliveryAreaId, active }) => {
        // Only a slot with no zone of its own (slotDeliveryAreaId falsy) applies regardless of
        // the cart's zone. A slot scoped to a zone must match the cart's deliveryAreaId exactly
        // -- an unresolved cart deliveryAreaId should not fall back to matching every zone.
        const deliveryAreaMatches = !slotDeliveryAreaId || deliveryAreaId === slotDeliveryAreaId;
        const timeMatches = currentMoment.isBefore(startTimeISO);
        return timeMatches && deliveryAreaMatches && active;
      }
    );
  } else {
    reservations = _.filter(reservationsAvailableByOrderType?.[normalizedOrderType] || [], ({ endTimeISO }) =>
      currentMoment.isBefore(endTimeISO)
    );
  }

  const availableReservations = _.map(reservations, (reservation, i) => {
    const { dayOfWeek, prefix, startTime, endTime } = getScheduledOrderTime({
      reservation,
      timezone,
    });
    return {
      dayOfWeek,
      value: i,
      labelPrefix: prefix,
      label: `${startTime} - ${endTime}`,
      isDisabled: reservation.quantityRemaining === 0,
      startTimeISO: reservation.startTimeISO,
      isNextDaySlot: reservation.isNextDaySlot,
      reservation,
    };
  });

  return availableReservations;
}

/**
 * Determines whether a weight change is valid and what the resulting item would be.
 * Does NOT perform any mutations -- the caller applies the result.
 *
 * @param {Object} params
 * @param {Object} params.item - the cart item whose weight is being changed
 * @param {string} params.option - the new weight option string
 * @param {boolean} params.isKioskOrder - whether this is a kiosk order
 * @param {Object} params.existingItems - plain JS snapshot of current cart items
 * @param {boolean} params.isMedical - whether this is a medical order
 * @param {Object} params.dispensary - dispensary data
 * @param {string|null} params.customerState - customer's state for compliance checks
 * @returns {{ action: 'update', newItem: Object } | { action: 'error', message: string }}
 */
export function getWeightChangeDecision({
  item,
  option,
  isKioskOrder,
  existingItems,
  isMedical,
  dispensary,
  customerState,
}) {
  const quantityRemaining = getQuantityRemaining(item.product, option, { isKiosk: isKioskOrder });

  const newItem = {
    ..._.clone(item),
    option,
    quantity: Math.min(quantityRemaining, item.quantity),
  };
  newItem.key = cartItemId(newItem);

  if (newItem.quantity === 0) {
    return { action: 'error', message: `Sorry, this weight is currently out of stock.` };
  }

  const existingCartItem = existingItems[newItem.key];

  if (existingCartItem && existingCartItem.quantity + newItem.quantity > quantityRemaining) {
    return {
      action: 'error',
      message: `Sorry, there is no more stock available for ${item.product.name} - ${option}.`,
    };
  }

  const limitsCheck = checkAllLimits({
    newItemId: newItem.key,
    itemBeingAdded: newItem,
    itemBeingRemoved: item.key,
    existingItems,
    medical: isMedical,
    dispensary,
    customerState,
    useMultiSubcategories: isMultiSubcategoryPurchaseLimitsEnabled(dispensary?.id),
    configurablePurchaseLimitsFeatureEnabled: isConfigurablePurchaseLimitsEnabled(dispensary?.id),
  });

  if (!limitsCheck.withinLimits) {
    return { action: 'error', message: limitsCheck.message };
  }

  return { action: 'update', newItem };
}
